/**
 * Validation utility functions for form inputs
 */

/**
 * Validates email address format
 * @param {string} email - Email address to validate
 * @returns {object} Validation result with isValid and message
 */
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return { isValid: false, message: 'Email is required' };
  }
  
  // Enhanced email regex for better validation
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!emailRegex.test(email)) {
    return { isValid: false, message: 'Invalid email format' };
  }
  
  if (email.length > 254) {
    return { isValid: false, message: 'Email address too long' };
  }
  
  return { isValid: true, message: '' };
};

/**
 * Validates password strength with security requirements
 * @param {string} password - Password to validate
 * @param {number} minLength - Minimum password length (default: 8)
 * @returns {object} Validation result with isValid, message, and strength score
 */
export const validatePassword = (password, minLength = 8) => {
  if (!password || typeof password !== 'string') {
    return { isValid: false, message: 'Password is required', strength: 0 };
  }
  
  const errors = [];
  let strength = 0;
  
  // Length check
  if (password.length < minLength) {
    errors.push(`Password must be at least ${minLength} characters long`);
  } else {
    strength += 1;
  }
  
  // Character variety checks
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  } else {
    strength += 1;
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  } else {
    strength += 1;
  }
  
  if (!/[0-9]/.test(password)) {
    errors.push('Password must contain at least one number');
  } else {
    strength += 1;
  }
  
  if (!/[^a-zA-Z0-9]/.test(password)) {
    errors.push('Password must contain at least one special character');
  } else {
    strength += 1;
  }
  
  // Length bonus
  if (password.length >= 12) strength += 1;
  if (password.length >= 16) strength += 1;
  
  const isValid = errors.length === 0 && password.length >= minLength;
  const message = errors.length > 0 ? errors[0] : '';
  
  return { isValid, message, strength: Math.min(strength, 5) };
};

/**
 * Validates that a field is not empty
 * @param {*} value - Value to validate
 * @returns {boolean} True if value is not empty
 */
export const validateRequired = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

/**
 * Validates package name format
 * @param {string} name - Package name to validate
 * @returns {boolean} True if valid package name
 */
export const validatePackageName = (name) => {
  if (!name || typeof name !== 'string') return false;
  
  // Package names should be lowercase, alphanumeric with dashes/underscores
  // Should not start with number or special character
  const packageNameRegex = /^[a-z][a-z0-9_-]*$/;
  return packageNameRegex.test(name);
};

/**
 * Validates semantic version format (x.y.z)
 * @param {string} version - Version string to validate
 * @returns {boolean} True if valid semantic version
 */
export const validateVersion = (version) => {
  if (!version || typeof version !== 'string') return false;
  
  const versionRegex = /^\d+\.\d+\.\d+$/;
  return versionRegex.test(version);
};

/**
 * Validates cron expression format
 * @param {string} cronExpr - Cron expression to validate
 * @returns {boolean} True if valid cron expression
 */
export const validateCronExpression = (cronExpr) => {
  if (!cronExpr || typeof cronExpr !== 'string') return false;
  
  const parts = cronExpr.trim().split(/\s+/);
  if (parts.length !== 5) return false;
  
  // Basic validation for each field
  const [minute, hour, day, month, weekday] = parts;
  
  // Minute: 0-59
  if (!isValidCronField(minute, 0, 59)) return false;
  
  // Hour: 0-23
  if (!isValidCronField(hour, 0, 23)) return false;
  
  // Day: 1-31
  if (!isValidCronField(day, 1, 31)) return false;
  
  // Month: 1-12
  if (!isValidCronField(month, 1, 12)) return false;
  
  // Weekday: 0-6 (Sunday = 0)
  if (!isValidCronField(weekday, 0, 6)) return false;
  
  return true;
};

/**
 * Helper function to validate individual cron fields
 * @param {string} field - Cron field to validate
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @returns {boolean} True if valid cron field
 */
const isValidCronField = (field, min, max) => {
  // Allow wildcards
  if (field === '*') return true;
  
  // Allow step values (*/n)
  if (field.startsWith('*/')) {
    const step = parseInt(field.substring(2));
    return !isNaN(step) && step > 0;
  }
  
  // Allow ranges (n-m)
  if (field.includes('-')) {
    const [start, end] = field.split('-').map(Number);
    return !isNaN(start) && !isNaN(end) && start >= min && end <= max && start <= end;
  }
  
  // Allow lists (n,m,o)
  if (field.includes(',')) {
    const values = field.split(',').map(Number);
    return values.every(val => !isNaN(val) && val >= min && val <= max);
  }
  
  // Single number
  const num = parseInt(field);
  return !isNaN(num) && num >= min && num <= max;
};

/**
 * Sanitizes input to prevent XSS attacks
 * @param {string} input - Input string to sanitize
 * @returns {string} Sanitized string
 */
export const sanitizeInput = (input) => {
  if (!input || typeof input !== 'string') return '';
  
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
};

/**
 * Validates and sanitizes text input
 * @param {string} text - Text to validate
 * @param {number} maxLength - Maximum allowed length
 * @param {boolean} required - Whether field is required
 * @returns {object} Validation result with isValid, message, and sanitizedValue
 */
export const validateText = (text, maxLength = 255, required = true) => {
  if (!text || typeof text !== 'string') {
    if (required) {
      return { isValid: false, message: 'This field is required', sanitizedValue: '' };
    }
    return { isValid: true, message: '', sanitizedValue: '' };
  }
  
  const trimmed = text.trim();
  
  if (required && trimmed.length === 0) {
    return { isValid: false, message: 'This field is required', sanitizedValue: '' };
  }
  
  if (trimmed.length > maxLength) {
    return { 
      isValid: false, 
      message: `Maximum ${maxLength} characters allowed`, 
      sanitizedValue: sanitizeInput(trimmed) 
    };
  }
  
  return { 
    isValid: true, 
    message: '', 
    sanitizedValue: sanitizeInput(trimmed) 
  };
};

/**
 * Validates username format
 * @param {string} username - Username to validate
 * @returns {object} Validation result with isValid and message
 */
export const validateUsername = (username) => {
  if (!username || typeof username !== 'string') {
    return { isValid: false, message: 'Username is required' };
  }
  
  const trimmed = username.trim();
  
  if (trimmed.length < 3) {
    return { isValid: false, message: 'Username must be at least 3 characters long' };
  }
  
  if (trimmed.length > 50) {
    return { isValid: false, message: 'Username must be less than 50 characters' };
  }
  
  // Allow alphanumeric, underscore, hyphen, and dot
  const usernameRegex = /^[a-zA-Z0-9._-]+$/;
  if (!usernameRegex.test(trimmed)) {
    return { isValid: false, message: 'Username can only contain letters, numbers, dots, underscores, and hyphens' };
  }
  
  return { isValid: true, message: '' };
};